import React, { useState, useEffect } from 'react';
import {
  FiX,
  FiAlertTriangle,
  FiGift,
  FiPause,
  FiArrowDown,
  FiMessageSquare,
  FiCheck,
  FiChevronRight,
  FiChevronLeft,
  FiPercent,
  FiClock,
  FiHeart
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { checkRetentionEligibility, applyRetentionOffer, RetentionOfferResponse } from '../../api/payments';
import RetentionOfferConfirmationModal from './RetentionOfferConfirmationModal';
import PlanComparisonModal from './PlanComparisonModal';

interface CancellationFlowModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmCancel: (reason: string, feedback: string) => Promise<void>;
  onAcceptOffer?: (offerType: 'discount' | 'pause' | 'downgrade') => Promise<void>;
  currentPlan: string;
  effectiveDate: string;
  showRetentionOffers?: boolean;
  discountPercent?: number;
  pauseDurationDays?: number;
}

type CancellationReason =
  | 'too_expensive'
  | 'not_using_enough'
  | 'missing_features'
  | 'switching_competitor'
  | 'temporary_pause'
  | 'company_closing'
  | 'other';

const CANCELLATION_REASON_ICONS: Record<CancellationReason, React.ReactNode> = {
  too_expensive: <FiPercent />,
  not_using_enough: <FiClock />,
  missing_features: <FiX />,
  switching_competitor: <FiArrowDown />,
  temporary_pause: <FiPause />,
  company_closing: <FiAlertTriangle />,
  other: <FiMessageSquare />,
};

type Step = 'reason' | 'offers' | 'feedback' | 'confirm';

const CancellationFlowModal: React.FC<CancellationFlowModalProps> = ({
  isOpen,
  onClose,
  onConfirmCancel,
  onAcceptOffer,
  currentPlan,
  effectiveDate,
  showRetentionOffers = true,
  discountPercent = 20,
  pauseDurationDays = 30,
}) => {
  const { t } = useTranslation('settings');
  const [step, setStep] = useState<Step>('reason');
  const [selectedReason, setSelectedReason] = useState<CancellationReason | null>(null);
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [acceptingOffer, setAcceptingOffer] = useState<string | null>(null);

  // Eligibility and modals
  const [isEligible, setIsEligible] = useState(true);
  const [eligibilityMessage, setEligibilityMessage] = useState<string | null>(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);

  // Confirmation modal for accepted offers
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmedOffer, setConfirmedOffer] = useState<RetentionOfferResponse | null>(null);

  // Comparison modal for downgrade
  const [showComparisonModal, setShowComparisonModal] = useState(false);

  const CANCELLATION_REASONS: CancellationReason[] = [
    'too_expensive',
    'not_using_enough',
    'missing_features',
    'switching_competitor',
    'temporary_pause',
    'company_closing',
    'other',
  ];

  // Check eligibility when moving to offers step
  useEffect(() => {
    if (step === 'offers' && showRetentionOffers) {
      const checkEligibility = async () => {
        setCheckingEligibility(true);
        try {
          const result = await checkRetentionEligibility();
          setIsEligible(result.eligible);
          setEligibilityMessage(result.message || null);
        } catch (error) {
          console.error('Failed to check eligibility:', error);
          // Allow offers to show even if check fails
          setIsEligible(true);
        } finally {
          setCheckingEligibility(false);
        }
      };
      checkEligibility();
    }
  }, [step, showRetentionOffers]);

  if (!isOpen) return null;

  const handleBack = () => {
    switch (step) {
      case 'offers':
        setStep('reason');
        break;
      case 'feedback':
        // Vérifier si les offres ont vraiment été affichées pour cette raison
        const hasOffersStep = showRetentionOffers && selectedReason &&
          (selectedReason === 'too_expensive' || selectedReason === 'temporary_pause' || selectedReason === 'not_using_enough');
        setStep(hasOffersStep ? 'offers' : 'reason');
        break;
      case 'confirm':
        setStep('feedback');
        break;
    }
  };

  const handleNext = () => {
    switch (step) {
      case 'reason':
        if (selectedReason) {
          // Si l'utilisateur trouve ça trop cher ou pause temporaire, proposer des offres
          if (showRetentionOffers && (selectedReason === 'too_expensive' || selectedReason === 'temporary_pause' || selectedReason === 'not_using_enough')) {
            setStep('offers');
          } else {
            setStep('feedback');
          }
        }
        break;
      case 'offers':
        setStep('feedback');
        break;
      case 'feedback':
        setStep('confirm');
        break;
    }
  };

  const handleAcceptOffer = async (offerType: 'discount' | 'pause' | 'downgrade') => {
    // For downgrade, show comparison modal first
    if (offerType === 'downgrade') {
      setShowComparisonModal(true);
      return;
    }

    // For discount/pause, apply directly
    setAcceptingOffer(offerType);
    try {
      const result = await applyRetentionOffer(offerType);

      if (result.success) {
        // Show confirmation modal with offer details
        setConfirmedOffer(result);
        setShowConfirmationModal(true);

        // Also call the parent callback if provided
        if (onAcceptOffer) {
          await onAcceptOffer(offerType);
        }
      }
    } catch (error) {
      console.error('Failed to accept offer:', error);
      // TODO: Show error toast
    } finally {
      setAcceptingOffer(null);
    }
  };

  const handleConfirmDowngrade = async () => {
    setShowComparisonModal(false);
    setAcceptingOffer('downgrade');

    try {
      const result = await applyRetentionOffer('downgrade');

      if (result.success) {
        // Show confirmation modal with offer details
        setConfirmedOffer(result);
        setShowConfirmationModal(true);

        // Also call the parent callback if provided
        if (onAcceptOffer) {
          await onAcceptOffer('downgrade');
        }
      }
    } catch (error) {
      console.error('Failed to accept downgrade offer:', error);
      // TODO: Show error toast
    } finally {
      setAcceptingOffer(null);
    }
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
    setConfirmedOffer(null);
    // Close the cancellation flow modal as well
    onClose();
  };

  const handleConfirmCancel = async () => {
    if (!selectedReason) return;

    setLoading(true);
    try {
      await onConfirmCancel(selectedReason, feedback);
      onClose();
    } catch (error) {
      console.error('Failed to cancel:', error);
    } finally {
      setLoading(false);
    }
  };

  const getReasonLabel = () => {
    return selectedReason ? t(`cancellation.reasonStep.reasons.${selectedReason}`) : '';
  };

  const renderStepIndicator = () => {
    // Déterminer si les offres seront affichées pour la raison sélectionnée
    const willShowOffers = showRetentionOffers && selectedReason &&
      (selectedReason === 'too_expensive' || selectedReason === 'temporary_pause' || selectedReason === 'not_using_enough');

    const steps = willShowOffers
      ? [t('cancellation.steps.reason'), t('cancellation.steps.offers'), t('cancellation.steps.feedback'), t('cancellation.steps.confirm')]
      : [t('cancellation.steps.reason'), t('cancellation.steps.feedback'), t('cancellation.steps.confirm')];

    const currentIndex = willShowOffers
      ? ['reason', 'offers', 'feedback', 'confirm'].indexOf(step)
      : ['reason', 'feedback', 'confirm'].indexOf(step);

    return (
      <div className="flex items-center justify-center gap-2 mb-6">
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors ${
              i <= currentIndex
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-200 text-gray-500'
            }`}>
              {i < currentIndex ? <FiCheck className="w-4 h-4" /> : i + 1}
            </div>
            {i < steps.length - 1 && (
              <div className={`w-8 h-1 rounded ${
                i < currentIndex ? 'bg-indigo-600' : 'bg-gray-200'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderReasonStep = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FiHeart className="w-8 h-8 text-orange-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">{t('cancellation.reasonStep.title')}</h3>
        <p className="text-gray-600 mt-2">{t('cancellation.reasonStep.subtitle')}</p>
      </div>

      <div className="space-y-2">
        {CANCELLATION_REASONS.map((reason) => (
          <button
            key={reason}
            onClick={() => setSelectedReason(reason)}
            className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${
              selectedReason === reason
                ? 'border-indigo-600 bg-indigo-50'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              selectedReason === reason
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-600'
            }`}>
              {CANCELLATION_REASON_ICONS[reason]}
            </div>
            <span className={`font-medium ${
              selectedReason === reason ? 'text-indigo-900' : 'text-gray-700'
            }`}>
              {t(`cancellation.reasonStep.reasons.${reason}`)}
            </span>
            {selectedReason === reason && (
              <FiCheck className="w-5 h-5 text-indigo-600 ml-auto" />
            )}
          </button>
        ))}
      </div>
    </div>
  );

  const renderOffersStep = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FiGift className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">{t('cancellation.offersStep.title')}</h3>
        <p className="text-gray-600 mt-2">{t('cancellation.offersStep.subtitle')}</p>
      </div>

      {/* Eligibility warning */}
      {checkingEligibility && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
          <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
          <p className="text-sm text-gray-600">Vérification de votre éligibilité...</p>
        </div>
      )}

      {!checkingEligibility && !isEligible && eligibilityMessage && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <FiAlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-800">Offres non disponibles</p>
              <p className="text-sm text-yellow-700 mt-1">{eligibilityMessage}</p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {/* Offre de réduction */}
        {selectedReason === 'too_expensive' && (
          <div className="p-5 rounded-xl border-2 border-green-200 bg-green-50">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <FiPercent className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-green-900 text-lg">{t('cancellation.offersStep.discount.title', { percent: discountPercent })}</h4>
                <p className="text-green-700 text-sm mt-1">
                  {t('cancellation.offersStep.discount.description', { percent: discountPercent })}
                </p>
                <button
                  onClick={() => handleAcceptOffer('discount')}
                  disabled={acceptingOffer !== null || !isEligible || checkingEligibility}
                  className="mt-3 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {acceptingOffer === 'discount' ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t('cancellation.offersStep.applying')}
                    </>
                  ) : (
                    <>
                      <FiCheck className="w-4 h-4" />
                      {t('cancellation.offersStep.discount.accept')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Offre de pause */}
        {(selectedReason === 'temporary_pause' || selectedReason === 'not_using_enough') && (
          <div className="p-5 rounded-xl border-2 border-blue-200 bg-blue-50">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <FiPause className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-blue-900 text-lg">{t('cancellation.offersStep.pause.title', { days: pauseDurationDays })}</h4>
                <p className="text-blue-700 text-sm mt-1">
                  {t('cancellation.offersStep.pause.description', { days: pauseDurationDays })}
                </p>
                <button
                  onClick={() => handleAcceptOffer('pause')}
                  disabled={acceptingOffer !== null || !isEligible || checkingEligibility}
                  className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {acceptingOffer === 'pause' ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t('cancellation.offersStep.applying')}
                    </>
                  ) : (
                    <>
                      <FiPause className="w-4 h-4" />
                      {t('cancellation.offersStep.pause.accept')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Offre de downgrade */}
        {currentPlan === 'entreprise' && (
          <div className="p-5 rounded-xl border-2 border-purple-200 bg-purple-50">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <FiArrowDown className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-purple-900 text-lg">{t('cancellation.offersStep.downgrade.title')}</h4>
                <p className="text-purple-700 text-sm mt-1">
                  {t('cancellation.offersStep.downgrade.description')}
                </p>
                <button
                  onClick={() => handleAcceptOffer('downgrade')}
                  disabled={acceptingOffer !== null || !isEligible || checkingEligibility}
                  className="mt-3 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {acceptingOffer === 'downgrade' ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {t('cancellation.offersStep.applying')}
                    </>
                  ) : (
                    <>
                      <FiArrowDown className="w-4 h-4" />
                      {t('cancellation.offersStep.downgrade.accept')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <p className="text-center text-gray-500 text-sm mt-4">
        {t('cancellation.offersStep.notInterested')}{' '}
        <button onClick={handleNext} className="text-indigo-600 hover:underline">
          {t('cancellation.offersStep.continueCancel')}
        </button>
      </p>
    </div>
  );

  const renderFeedbackStep = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FiMessageSquare className="w-8 h-8 text-indigo-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">{t('cancellation.feedbackStep.title')}</h3>
        <p className="text-gray-600 mt-2">{t('cancellation.feedbackStep.subtitle')}</p>
      </div>

      <div className="bg-gray-50 rounded-xl p-4 mb-4">
        <p className="text-sm text-gray-600">
          <span className="font-medium">{t('cancellation.feedbackStep.selectedReason')} :</span>{' '}
          <span className="text-gray-900">{getReasonLabel()}</span>
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('cancellation.feedbackStep.additionalComments')}
        </label>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder={t('cancellation.feedbackStep.placeholder')}
          rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-gray-900 placeholder-gray-400 bg-white"
        />
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <FiAlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-800">
            <p className="font-medium">{t('cancellation.feedbackStep.reminder.title')}</p>
            <p className="mt-1">
              {t('cancellation.feedbackStep.reminder.message', { date: effectiveDate })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderConfirmStep = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FiAlertTriangle className="w-8 h-8 text-red-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">{t('cancellation.confirmStep.title')}</h3>
        <p className="text-gray-600 mt-2">{t('cancellation.confirmStep.subtitle')}</p>
      </div>

      <div className="bg-gray-50 rounded-xl p-5 space-y-3">
        <div className="flex justify-between">
          <span className="text-gray-600">{t('cancellation.confirmStep.summary.currentPlan')}</span>
          <span className="font-medium text-gray-900 capitalize">{currentPlan}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('cancellation.confirmStep.summary.reason')}</span>
          <span className="font-medium text-gray-900">{getReasonLabel()}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('cancellation.confirmStep.summary.endDate')}</span>
          <span className="font-medium text-gray-900">{effectiveDate}</span>
        </div>
        {feedback && (
          <div className="pt-3 border-t border-gray-200">
            <span className="text-gray-600 text-sm">Feedback :</span>
            <p className="text-gray-900 text-sm mt-1">{feedback}</p>
          </div>
        )}
      </div>

      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <FiAlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">
            <p className="font-medium">{t('cancellation.confirmStep.consequences.title')}</p>
            <ul className="mt-2 space-y-1 list-disc list-inside">
              <li>{t('cancellation.confirmStep.consequences.cancelAtEnd')}</li>
              <li>{t('cancellation.confirmStep.consequences.switchToFree')}</li>
              <li>{t('cancellation.confirmStep.consequences.projectsArchived')}</li>
              <li>{t('cancellation.confirmStep.consequences.canResubscribe')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 'reason':
        return renderReasonStep();
      case 'offers':
        return renderOffersStep();
      case 'feedback':
        return renderFeedbackStep();
      case 'confirm':
        return renderConfirmStep();
    }
  };

  const canProceed = () => {
    switch (step) {
      case 'reason':
        return selectedReason !== null;
      case 'offers':
        return true;
      case 'feedback':
        return true;
      case 'confirm':
        return true;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{t('cancellation.title')}</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {renderStepIndicator()}
          {renderStep()}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          {step !== 'reason' ? (
            <button
              onClick={handleBack}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors inline-flex items-center gap-2"
            >
              <FiChevronLeft className="w-4 h-4" />
              {t('cancellation.buttons.back')}
            </button>
          ) : (
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
            >
              {t('cancellation.buttons.cancel')}
            </button>
          )}

          {step === 'confirm' ? (
            <button
              onClick={handleConfirmCancel}
              disabled={loading}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 inline-flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t('cancellation.buttons.cancelling')}
                </>
              ) : (
                <>
                  <FiX className="w-4 h-4" />
                  {t('cancellation.buttons.confirm')}
                </>
              )}
            </button>
          ) : step !== 'offers' ? (
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {t('cancellation.buttons.continue')}
              <FiChevronRight className="w-4 h-4" />
            </button>
          ) : null}
        </div>
      </div>

      {/* Retention Offer Confirmation Modal */}
      {showConfirmationModal && confirmedOffer && (
        <RetentionOfferConfirmationModal
          isOpen={showConfirmationModal}
          onClose={handleCloseConfirmationModal}
          offerType={confirmedOffer.offer.offer_type}
          offerDetails={{
            discountPercent: confirmedOffer.offer.discount_percent,
            pauseDays: confirmedOffer.offer.pause_days,
            targetPlan: confirmedOffer.offer.target_plan,
            expiresAt: confirmedOffer.offer.expires_at,
            appliedAt: confirmedOffer.offer.applied_at,
            message: confirmedOffer.message
          }}
        />
      )}

      {/* Plan Comparison Modal for Downgrade */}
      {showComparisonModal && (
        <PlanComparisonModal
          isOpen={showComparisonModal}
          onClose={() => setShowComparisonModal(false)}
          onConfirm={handleConfirmDowngrade}
          currentPlan={currentPlan}
          targetPlan="standard"
          isLoading={acceptingOffer === 'downgrade'}
        />
      )}
    </div>
  );
};

export default CancellationFlowModal;
